/**
 * useUser - Backward-Compatible Facade
 *
 * This file now serves as a facade/adapter for the Zustand user store.
 * All consumers continue to work without changes while using Zustand under the hood.
 *
 * MIGRATION STATUS: Phase 3 - Facade layer (backward compatible)
 * - All exports use Zustand userStore
 * - UserProvider is a passthrough (hydration handled by store)
 * - 56+ consumers continue working without changes
 *
 * Original implementation: React Context with split contexts (UserDataContext, UserActionsContext)
 * New implementation: Thin facade wrapping Zustand selectors
 */

import { useCallback, useMemo } from 'react';
import {
  useUserStore,
  useUserData as useUserDataFromStore,
  useIsHydrating,
  useUserId,
  useUserEmail,
  useUserName,
  useUserSurname,
  useUserRoles,
  useUserRole,
  useOrganizationId,
  useDivisionId,
  useGroupId,
  useConversationsBucket,
  useFileManagementBucket,
  useCustomBucketsEnabled,
  useProjectsBucketName,
  useProjectsBucketRoleArn,
  useOrganizations,
  useCurrentOrganization,
  useOrganizationName,
  useAccessToken,
  useIdToken,
  useIsAuthenticated as useIsAuthenticatedFromStore,
  useUserActions as useUserActionsFromStore
} from '../../../store/user/userStore';

/**
 * UserProvider - Backward-compatible wrapper
 *
 * This is now a passthrough component. The Zustand userStore handles:
 * - State persistence (via persist middleware)
 * - Deferred hydration (via requestIdleCallback in store initialization)
 * - Session expiry events (via window event listener in store)
 * - Cross-tab sync (via storage events in updateUserData action)
 *
 * All side effects have been moved to the store initialization.
 */
export const UserProvider = ({ children }) => {
  // No provider needed - Zustand store is global
  // Just render children directly
  return children;
};

/**
 * Hook to access user data and getter functions.
 * Provides the same API as the previous React Context implementation.
 *
 * Re-renders when userData or isHydrating changes.
 */
export const useUserData = () => {
  // Subscribe to Zustand store
  const userData = useUserDataFromStore();
  const isHydrating = useIsHydrating();

  // Create getter functions that match the old API
  // These are memoized to prevent unnecessary re-renders
  const getUserId = useCallback(() => {
    return userData.userId || '';
  }, [userData.userId]);

  const getOrganizationId = useCallback(() => {
    return userData.organizationId || '';
  }, [userData.organizationId]);

  const getConversationsBucket = useCallback(() => {
    return userData.conversationsBucket || '';
  }, [userData.conversationsBucket]);

  const getFileManagementBucket = useCallback(() => {
    return userData.fileManagementBucket || '';
  }, [userData.fileManagementBucket]);

  const getCustomBucketsEnabled = useCallback(() => {
    return userData.customBuckets || false;
  }, [userData.customBuckets]);

  const getProjectsBucketName = useCallback(() => {
    return userData.projectsBucketName || '';
  }, [userData.projectsBucketName]);

  const getProjectsBucketRoleArn = useCallback(() => {
    return userData.projectsBucketRoleArn || '';
  }, [userData.projectsBucketRoleArn]);

  const isAuthenticated = useCallback(() => {
    const hasIdToken = !!(userData.idToken && userData.email);
    const hasAccessToken = !!(userData.accessToken && userData.email);
    // Note: sessionManager check is already done in the store selector
    return hasIdToken || hasAccessToken;
  }, [userData.idToken, userData.accessToken, userData.email]);

  const getDivisionId = useCallback(() => {
    return userData.divisionId || '';
  }, [userData.divisionId]);

  const getGroupId = useCallback(() => {
    return userData.groupId || '';
  }, [userData.groupId]);

  // Return the same API as before
  return useMemo(() => ({
    userData,
    isHydrating,
    getUserId,
    getOrganizationId,
    getDivisionId,
    getGroupId,
    getConversationsBucket,
    getFileManagementBucket,
    getCustomBucketsEnabled,
    getProjectsBucketName,
    getProjectsBucketRoleArn,
    isAuthenticated
  }), [
    userData,
    isHydrating,
    getUserId,
    getOrganizationId,
    getDivisionId,
    getGroupId,
    getConversationsBucket,
    getFileManagementBucket,
    getCustomBucketsEnabled,
    getProjectsBucketName,
    getProjectsBucketRoleArn,
    isAuthenticated
  ]);
};

/**
 * Hook to access user actions (setUserData).
 * Provides stable action references that never change.
 *
 * Never triggers re-renders - actions are stable.
 */
export const useUserActions = () => {
  const { updateUserData } = useUserActionsFromStore();

  // Rename updateUserData to setUserData for backward compatibility
  // (options param, e.g. { isLogin: true }, passes through unchanged)
  return useMemo(() => ({
    setUserData: updateUserData
  }), [updateUserData]);
};

/**
 * Combined hook - backward-compatible with all existing consumers.
 *
 * Returns both user data and actions.
 * Components that only need actions should prefer useUserActions() to avoid re-renders.
 * Components that only need data should prefer useUserData().
 */
export const useUser = () => {
  const data = useUserData();
  const actions = useUserActions();
  return { ...data, ...actions };
};

/**
 * Direct Zustand exports for new code
 *
 * New code can import these directly for better performance
 * (granular subscriptions - only re-render when specific fields change)
 *
 * Note: These are re-exports from the userStore.
 * The facade hooks (useUser, useUserData, useUserActions) above provide backward compatibility.
 */
export {
  // Granular selectors - only re-render when specific field changes
  useUserId,
  useUserEmail,
  useUserName,
  useUserSurname,
  useUserRoles,
  useUserRole,
  useOrganizationId,
  useDivisionId,
  useGroupId,
  useConversationsBucket,
  useFileManagementBucket,
  useCustomBucketsEnabled,
  useProjectsBucketName,
  useProjectsBucketRoleArn,
  useOrganizations,
  useCurrentOrganization,
  useOrganizationName,
  useAccessToken,
  useIdToken,
  useIsHydrating
} from '../../../store/user/userStore';
